/**
 * Admins dashboard section: tabbed list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LayoutGrid } from 'lucide-react';
import { AdminsDataTable } from '@/components/admins/AdminsDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateAdminView } from '@/components/admins/CreateAdminView';
import { EditAdminView } from '@/components/admins/EditAdminView';
import { Select } from '@/components/forms/Select';
import { usePageActions } from '@/hooks/usePageActions';
import { useRoleGroupTabs } from '@/hooks/useRoleGroupTabs';
import { DynamicIcon } from '@/components/forms/IconPicker';
import { ADMIN_TAB_COLORS } from '@/components/admins/admin-configs';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link AdminsSection}. */
interface AdminsSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: admins list. */
export const AdminsSection: React.FC<AdminsSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const queryClient = useQueryClient();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editAdminId, setEditAdminId] = React.useState<string | null>(null);

  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('admins');
  const { visibleTabs, showTabBar, activeRoleFilter, setActiveRoleFilter } = useRoleGroupTabs(
    'admins',
    'menu-admins',
  );

  /** Role-code filter for the table; empty when the All tab is selected. */
  const enabledRoles: string[] = activeRoleFilter === 'all' ? [] : [activeRoleFilter];

  const handleCreateAdmin = () => {
    setCreateFormOpen(true);
    setEditAdminId(null);
  };

  const handleEditAdmin = (id: string) => {
    setEditAdminId(id);
    setCreateFormOpen(false);
  };

  const handleSuccess = () => {
    setCreateFormOpen(false);
    setEditAdminId(null);
    void queryClient.invalidateQueries({ queryKey: ['users', 'admin'] });
    onSuccess?.();
  };

  const tabsNode = React.useMemo(
    () =>
      showTabBar ? (
        <Select
          value={activeRoleFilter}
          onChange={setActiveRoleFilter}
          size="sm"
          options={[
            {
              value: 'all',
              label: 'Tous',
              icon: <LayoutGrid className="h-4 w-4" />,
              color: ADMIN_TAB_COLORS.all,
            },
            ...visibleTabs.map((tab) => ({
              value: tab.id,
              label: tab.label,
              icon: <DynamicIcon name={tab.icon} className="h-4 w-4" />,
              color: tab.color ?? undefined,
            })),
          ]}
        />
      ) : undefined,
    [showTabBar, activeRoleFilter, setActiveRoleFilter, visibleTabs],
  );

  return (
    <>
      <AdminsDataTable
        enabledRoles={enabledRoles}
        layoutWidth={layoutWidth}
        isSection={true}
        onEditAdmin={handleEditAdmin}
        showCreateButton={canCreate}
        onCreateAdmin={handleCreateAdmin}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
        toolbarLeft={tabsNode}
      />

      <SlidePanel isOpen={createFormOpen} onClose={() => setCreateFormOpen(false)}>
        <CreateAdminView onClose={() => setCreateFormOpen(false)} onSuccess={handleSuccess} />
      </SlidePanel>

      <SlidePanel isOpen={editAdminId !== null} onClose={() => setEditAdminId(null)}>
        {editAdminId && (
          <EditAdminView
            key={editAdminId}
            adminId={editAdminId}
            onClose={() => setEditAdminId(null)}
            onSuccess={handleSuccess}
          />
        )}
      </SlidePanel>
    </>
  );
};
